import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductImageGallery from './components/ProductImageGallery';
import SellerProfile from './components/SellerProfile';
import AntiFraudWarning from './components/AntiFraudWarning';
import RelatedProducts from './components/RelatedProducts';
import './ProductDetails.css';

const API_BASE_URL = 'https://api.autocasses.com/api';

const ProductDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [produit, setProduit] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [sellerInfo, setSellerInfo] = useState(null);

  // Récupérer l'ID du produit depuis le state de navigation
  const productId = location.state?.productId;
  const initialProductData = location.state?.productData;

  useEffect(() => {
    loadUserToken();
  }, []);

  useEffect(() => {
    if (userToken !== null && productId) {
      fetchProductData();
    }
  }, [userToken, productId, retryCount]);

  // Si on a des données initiales, on les utilise
  useEffect(() => {
    if (initialProductData && !produit) {
      handleInitialProductData(initialProductData);
    }
  }, [initialProductData]);

  const handleInitialProductData = async (productData) => {
    try {
      setChargement(true);
      
      // On peut utiliser les données initiales pour afficher rapidement
      const tempData = await transformApiData(productData, null, null, null);
      setProduit(tempData);
      
      // Ensuite on charge les données complètes
      if (productData.id) {
        fetchProductData();
      } else {
        setChargement(false);
      }
    } catch (error) {
      console.error('Erreur lors du traitement des données initiales:', error);
      setChargement(false);
    }
  };

  const loadUserToken = async () => {
    try {
      const token = localStorage.getItem('userToken');
      setUserToken(token);
    } catch (error) {
      console.error(t('productDetails.errors.loadToken'), error);
      setUserToken(null);
    }
  };

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (userToken) {
      headers['Authorization'] = `Bearer ${userToken}`;
    }
    
    return headers;
  };

  const fetchCategory = async (categoryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories/getone/${categoryId}`, {
        headers: getAuthHeaders(),
        timeout: 8000,
      });
      return response.data;
    } catch (err) {
      console.error(t('productDetails.errors.fetchCategory', { categoryId }), err);
      return null;
    }
  };

  const fetchSousCategories = async (subCategoryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/souscat/${subCategoryId}`, {
        headers: getAuthHeaders(),
        timeout: 8000,
      });
      return response.data.data;
    } catch (err) {
      console.error(t('productDetails.errors.fetchSubCategory', { subCategoryId }), err);
      return null;
    }
  };

  const fetchProduct = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/produit/produits/${id}`, { 
        headers: getAuthHeaders(),
        timeout: 10000,
      });
      return response.data;
    } catch (err) {
      console.error(t('productDetails.errors.fetchProduct'), err);
      throw err;
    }
  };

  const fetchSellerInfo = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/get/${userId}`, { 
        headers: getAuthHeaders(),
        timeout: 10000,
      });
      
      if (response.data && typeof response.data === 'object') {
        return response.data;
      } else {
        console.warn(t('productDetails.warnings.invalidSellerResponse'));
        return null;
      }
    } catch (err) {
      console.error(t('productDetails.errors.fetchSeller'), err);
      
      if (err.response && err.response.status === 401) {
        console.log(t('productDetails.logs.tokenExpired'));
        
        try {
          const responseWithoutToken = await axios.get(`${API_BASE_URL}/user/set/${userId}`, {
            timeout: 10000,
          });
          
          if (responseWithoutToken.data && typeof responseWithoutToken.data === 'object') {
            return responseWithoutToken.data;
          }
        } catch (errorWithoutToken) {
          console.error(t('productDetails.errors.fetchWithoutToken'), errorWithoutToken);
        }
      }
      
      return null;
    }
  };

  const transformApiData = async (apiData, categoryName, subCategoryName, sellerInfo) => {
    const getCondition = (etat) => {
      const etatMap = {
        'neuf': t('productDetails.conditions.new'),
        'neuve': t('productDetails.conditions.newFeminine'),
        'occasion': t('productDetails.conditions.used'),
        'bon': t('productDetails.conditions.good'),
        'excellent': t('productDetails.conditions.excellent'),
        'moyen': t('productDetails.conditions.average'),
        'mauvais': t('productDetails.conditions.poor'),
        'réparé': t('productDetails.conditions.repaired'),
        'reconditionné': t('productDetails.conditions.refurbished')
      };
      return etatMap[apiData.etat?.toLowerCase()] || apiData.etat || t('productDetails.conditions.unknown');
    };

    const getImages = () => {
      if (!apiData.photos || apiData.photos.length === 0) {
        return [{
          url: "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop",
          alt: apiData.titre || t('productDetails.defaults.productImage')
        }];
      }
      return apiData.photos.map(photo => ({
        url: photo.photo_url || photo.url || '',
        alt: apiData.titre || t('productDetails.defaults.productImage')
      })).filter(img => img.url);
    };

    const getSeller = () => {
      const sellerData = sellerInfo || apiData.user;
      const nomVendeur = sellerData?.nom || t('productDetails.defaults.unknownSeller');
      
      const getDefaultAvatar = (name) => {
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
        const colorIndex = name.charCodeAt(0) % colors.length;
        
        const initials = name
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);
        
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[colorIndex].replace('#', '')}&color=fff&size=256`;
      };

      let sellerAvatar;
      if (sellerData?.avatar) {
        if (sellerData.avatar.startsWith('http')) {
          sellerAvatar = sellerData.avatar;
        } else {
          sellerAvatar = `${API_BASE_URL.replace('/api', '')}/uploads/avatars/${sellerData.avatar}`;
        }
      } else {
        sellerAvatar = getDefaultAvatar(nomVendeur);
      }

      let whatsappNumber = null;
      let phoneNumber = null;
      const telephone = sellerData?.telephone || apiData.user?.telephone;
      
      if (telephone) {
        let tel = telephone.toString().replace(/\s+/g, '');
        
        if (tel.startsWith('0')) {
          tel = '+237' + tel.substring(1);
        } else if (tel.match(/^[679]/)) {
          tel = '+237' + tel;
        } else if (tel.startsWith('237')) {
          tel = '+' + tel;
        } else if (!tel.startsWith('+')) {
          tel = '+237' + tel;
        }
        
        whatsappNumber = tel;
        phoneNumber = tel;
      }

      const typePro = sellerData?.type_pro || 'particulier';
      const isPro = sellerData?.isPro || typePro !== 'particulier';
      const isVerified = sellerData?.isVerified || false;

      return {
        id: sellerData?.id || apiData.user?.id || 0,
        name: nomVendeur,
        avatar: sellerAvatar,
        telephone: telephone,
        email: sellerData?.email || apiData.user?.email,
        type_pro: typePro,
        isPro: isPro,
        isVerified: isVerified,
        descriptions: sellerData?.descriptions,
        adresse: sellerData?.adresse,
        specialites: sellerData?.specialites,
        businessAddress: sellerData?.businessAddress,
        businessEmail: sellerData?.businessEmail,
        nom_pro: sellerData?.nom_pro,
        tel_pro: sellerData?.tel_pro,
        verificationLevel: isVerified ? 'verified' : 'basic',
        rating: 4.0,
        reviewCount: 0,
        totalListings: 1,
        completedSales: 0,
        responseTime: t('productDetails.seller.responseTime'),
        joinDate: sellerData?.createdAt || apiData.createdAt || '2024-01-01',
        isOnline: false,
        identityVerified: isVerified,
        phoneVerified: !!telephone,
        businessLicense: !!sellerData?.nom_pro,
        whatsappNumber: whatsappNumber,
        phoneNumber: phoneNumber,
        location: {
          city: apiData.city || sellerData?.adresse?.split(',')[0] || t('productDetails.defaults.unknownCity'),
          region: apiData.region || t('productDetails.defaults.unknownRegion'),
          coordinates: { lat: 0, lng: 0 }
        },
        bio: sellerData?.descriptions || t('productDetails.seller.defaultBio'),
        businessHours: sellerData?.businessHours || {
          lundi: t('productDetails.seller.businessHours.monday'),
          mardi: t('productDetails.seller.businessHours.tuesday'),
          mercredi: t('productDetails.seller.businessHours.wednesday'),
          jeudi: t('productDetails.seller.businessHours.thursday'),
          vendredi: t('productDetails.seller.businessHours.friday'),
          samedi: t('productDetails.seller.businessHours.saturday'),
          dimanche: t('productDetails.seller.businessHours.sunday')
        }
      };
    };

    return {
      id: apiData.id.toString(),
      title: apiData.titre || t('productDetails.defaults.untitled'),
      category: categoryName || t('productDetails.defaults.category', { id: apiData.categorieId }),
      categorieId: apiData.categorieId,
      subcategory: subCategoryName || t('productDetails.defaults.subcategory', { id: apiData.souscategorieId }),
      price: parseInt(apiData.prix) || 0,
      originalPrice: null,
      condition: getCondition(apiData.etat),
      brand: apiData.numpiece || t('productDetails.defaults.unspecifiedBrand'),
      partNumber: apiData.numpiece,
      inStock: !apiData.inActive,
      negociable: apiData.negociable === 'true' || apiData.negociable === true,
      echange: apiData.echange === 'true' || apiData.echange === true,
      isPremium: false,
      warranty: apiData.garantie ? t('productDetails.warranty.months', { months: apiData.garantie }) : t('productDetails.warranty.none'),
      description: apiData.description || t('productDetails.defaults.noDescription'),
      images: getImages(),
      specifications: {
        [t('productDetails.specifications.partNumber')]: apiData.numpiece || 'N/A',
        [t('productDetails.specifications.warranty')]: apiData.garantie ? t('productDetails.specifications.warrantyMonths', { months: apiData.garantie }) : 'N/A',
        [t('productDetails.specifications.exchange')]: apiData.echange === 'true' ? t('commonsssss.yes') : t('commonsssss.no'),
        [t('productDetails.specifications.negotiable')]: apiData.negociable === 'true' ? t('commonsssss.yes') : t('commonsssss.no'),
        [t('productDetails.specifications.country')]: apiData.pays || 'N/A',
        [t('productDetails.specifications.city')]: apiData.city || 'N/A',
        [t('productDetails.specifications.address')]: apiData.adresse_detail || 'N/A'
      },
      tags: ['auto', 'piece'],
      seller: getSeller()
    };
  };

  const fetchProductData = async () => {
    if (!productId) {
      setErreur(t('productDetails.errors.noProductId'));
      setChargement(false);
      return;
    }

    setChargement(true);
    setErreur(null);

    try {
      const productData = await fetchProduct(productId);
      
      if (!productData) {
        throw new Error(t('productDetails.errors.productNotFound'));
      }

      let sellerInfoData = null;
      const userId = productData.user?.id;
      
      if (userId) {
        sellerInfoData = await fetchSellerInfo(userId);
        console.log(t('productDetails.logs.sellerInfoRetrieved', { hasInfo: sellerInfoData ? 'yes' : 'no' }));
        setSellerInfo(sellerInfoData);
      } else {
        console.log(t('productDetails.logs.noUserId'));
      }

      const categoryId = productData.categorieId;
      const subCategoryId = productData.souscategorieId;

      let categoryName = null;
      let subCategoryName = null;

      if (categoryId) {
        try {
          const categoryResponse = await fetchCategory(categoryId);
          categoryName = categoryResponse?.nom || null;
          
          if (subCategoryId && categoryResponse) {
            const sousCategories = await fetchSousCategories(subCategoryId);
            subCategoryName = sousCategories?.nom;
          }
        } catch (catErr) {
          console.warn(t('productDetails.warnings.categoryDetails'), catErr);
        }
      }

      const transformedData = await transformApiData(
        productData, 
        categoryName, 
        subCategoryName,
        sellerInfoData
      );
      
      setProduit(transformedData);
      
    } catch (err) {
      console.error(t('productDetails.errors.loadingProduct'), err);
      
      let errorMessage = t('productDetails.errors.loadingData');
      
      if (err.response) {
        switch (err.response.status) {
          case 401:
            errorMessage = t('productDetails.errors.sessionExpired');
            if (window.confirm(t('productDetails.alerts.sessionExpiredMessage'))) {
              navigate('/login');
            }
            break;
          case 404:
            errorMessage = t('productDetails.errors.productNotFound');
            break;
          case 500:
            errorMessage = t('productDetails.errors.serverError');
            break;
          default:
            errorMessage = t('productDetails.errors.errorStatus', { status: err.response.status });
        }
      } else if (err.request) {
        errorMessage = t('productDetails.errors.noConnection');
      } else {
        errorMessage = t('productDetails.errors.errorWithMessage', { message: err.message });
      }
      
      setErreur(errorMessage);
      
      if (err.response?.status === 401 && retryCount === 0) {
        if (window.confirm(t('productDetails.alerts.retryWithoutAuth'))) {
          localStorage.removeItem('userToken');
          setUserToken(null);
          setRetryCount(1);
        }
      }
    } finally {
      setChargement(false);
    }
  };

  const handleShareProduct = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: produit?.title,
          text: `Découvrez "${produit?.title}" sur AutoCasses - ${formatPrice(produit?.price)}`,
          url: window.location.href,
        });
      } else {
        navigator.clipboard.writeText(
          `${produit?.title} - ${formatPrice(produit?.price)} - ${window.location.href}`
        );
        alert('Lien copié dans le presse-papier');
      }
    } catch (error) {
      console.error('Erreur de partage:', error);
    }
  };

  const handleWhatsAppContact = async () => {
    if (!produit?.seller?.whatsappNumber) {
      alert('Numéro WhatsApp non disponible pour ce vendeur');
      return;
    }

    const message = encodeURIComponent(
      `Bonjour, je suis intéressé par votre annonce "${produit?.title}" à ${formatPrice(produit?.price)} sur AutoCasses.`
    );
    
    const whatsappUrl = `https://wa.me/${produit.seller.whatsappNumber.replace('+', '')}?text=${message}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePhoneCall = () => {
    if (!produit?.seller?.phoneNumber) {
      alert('Numéro de téléphone non disponible');
      return;
    }
    window.location.href = `tel:${produit?.seller?.phoneNumber}`;
  };

  const handleSellerClick = (seller) => {
    if (seller?.id) {
      navigate(`/user/${seller.id}`, { 
        state: { 
          userName: seller.name 
        }
      });
    } else {
      alert('Informations du vendeur non disponibles');
    }
  };

  const toggleFavorite = async () => {
    if (!userToken) {
      if (window.confirm('Vous devez être connecté pour ajouter aux favoris. Voulez-vous vous connecter ?')) {
        navigate('/login');
      }
      return;
    }

    const token = localStorage.getItem('userToken');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/favoris/toggle`,
        { produitId: produit.id },
        { headers }
      );
      
      setIsFavorite(!isFavorite);
      
      // Notification
      await axios.post(
        `${API_BASE_URL}/notification/add`,
        { 
          title: isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris',
          messages: `Produit "${produit.title}" ${isFavorite ? 'retiré de' : 'ajouté à'} vos favoris`,
          type: "Favoris",
        },
        { headers }
      );
      
    } catch (error) {
      console.error('Erreur lors de la modification des favoris:', error);
      alert('Erreur lors de la modification des favoris');
    }
  };
  
  const retryFetch = () => {
    setRetryCount(prev => prev + 1);
  };

  const formatPrice = (price) => {
    return parseInt(price).toLocaleString('fr-FR') + ' FCFA';
  };

  if (chargement && !produit) {
    return (
      <div className="product-details-container">
        <div className="center-container">
          <div className="loader"></div>
          <div className="loading-text">
            Chargement du produit...
          </div>
        </div>
      </div>
    );
  }

  if (erreur && !produit) {
    return (
      <div className="product-details-container">
        <div className="center-container">
          <Icon name="AlertCircle" size={48} color="#ef4444" />
          <div className="error-text">{erreur}</div>
          <button onClick={retryFetch} className="retry-button">
            Réessayer
          </button>
          <button 
            onClick={() => navigate(-1)} 
            className="back-button-outline"
          >
            Retour
          </button>
          {erreur.includes(t('productDetails.errors.sessionExpired')) && (
            <button 
              onClick={() => navigate('/login')}
              className="login-button"
            >
              Se reconnecter
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!produit && !chargement) {
    return (
      <div className="product-details-container">
        <div className="center-container">
          <div className="error-text">
            Produit non trouvé
          </div>
          <button onClick={() => navigate(-1)} className="back-button">
            Retour à la recherche
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details-container desktop-layout">
      {/* Header avec breadcrumb */}
      <div className="product-header">
        <div className="breadcrumb">
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            Accueil
          </button>
          <span className="breadcrumb-separator">›</span>
          <button onClick={() => navigate('/rechercher')} className="breadcrumb-link">
            Recherche
          </button>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{produit.category}</span>
          {produit.subcategory && (
            <>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{produit.subcategory}</span>
            </>
          )}
        </div>
        
        <div className="header-actions">
          <button 
            className="action-icon"
            onClick={handleShareProduct}
            title="Partager"
          >
            <Icon name="Share2" size={20} color="#64748b" />
          </button>
          <button 
            className="action-icon favorite-icon"
            onClick={toggleFavorite}
            title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            <Icon 
              name={isFavorite ? "Heart" : "Heart"} 
              size={20} 
              color={isFavorite ? "#ef4444" : "#64748b"} 
              fill={isFavorite ? "#ef4444" : "none"}
            />
          </button>
          <button 
            className="back-button-desktop"
            onClick={() => navigate(-1)}
          >
            <Icon name="ArrowLeft" size={18} color="#3b82f6" />
            <span>Retour</span>
          </button>
        </div>
      </div>

      <div className="product-main-grid">
        {/* Colonne gauche - Images et informations principales */}
        <div className="left-column">
          <div className="sticky-section">
            <ProductImageGallery
              images={produit.images}
              productName={produit.title}
            />

            <div className="seller-info-sidebar">
              <SellerProfile 
                seller={produit.seller} 
                onClick={() => handleSellerClick(produit.seller)} 
                compact={true}
              />
              
              <div className="contact-buttons-sidebar">
                <button 
                  className="contact-button whatsapp-button"
                  onClick={handleWhatsAppContact}
                >
                  <Icon name="MessageCircle" size={18} color="#fff" />
                  <span>WhatsApp</span>
                </button>
                
                <button 
                  className="contact-button phone-button"
                  onClick={handlePhoneCall}
                >
                  <Icon name="Phone" size={18} color="#fff" />
                  <span>Appeler</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Colonne centrale - Détails du produit */}
        <div className="center-column">
          <div className="product-main-info">
            <h1 className="product-title-desktop">{produit.title}</h1>
            
            <div className="price-section">
              <div className="price-main">{formatPrice(produit.price)}</div>
              <div className="product-tags">
                <span className="tag condition-tag">
                  <Icon name="CheckCircle" size={14} color="#10b981" />
                  {produit.condition}
                </span>
                {produit.negociable && (
                  <span className="tag negociable-tag">
                    <Icon name="Handshake" size={14} color="#3b82f6" />
                    Négociable
                  </span>
                )}
                {produit.echange && (
                  <span className="tag exchange-tag">
                    <Icon name="RefreshCw" size={14} color="#f59e0b" />
                    Échange possible
                  </span>
                )}
                {produit.warranty !== t('productDetails.warranty.none') && (
                  <span className="tag warranty-tag">
                    <Icon name="ShieldCheck" size={14} color="#10b981" />
                    {produit.warranty}
                  </span>
                )}
              </div>
            </div>

            <div className="description-section">
              <h3 className="section-title-desktop">Description</h3>
              <div className="description-content">
                <p className="description-text-desktop">
                  {produit.description}
                </p>
                {produit.description && produit.description.length > 500 && (
                  <button 
                    className="read-more-link"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                  >
                    {showFullDescription ? 'Voir moins' : 'Lire la suite'}
                  </button>
                )}
              </div>
            </div>

            <div className="specifications-section">
              <h3 className="section-title-desktop">Caractéristiques</h3>
              <div className="specs-grid-desktop">
                <div className="spec-item-desktop">
                  <Icon name="Package" size={18} color="#6b7280" />
                  <div className="spec-content">
                    <div className="spec-label-desktop">Catégorie</div>
                    <div className="spec-value-desktop">{produit.category}</div>
                  </div>
                </div>
                
                <div className="spec-item-desktop">
                  <Icon name="Layers" size={18} color="#6b7280" />
                  <div className="spec-content">
                    <div className="spec-label-desktop">Sous-catégorie</div>
                    <div className="spec-value-desktop">{produit.subcategory}</div>
                  </div>
                </div>
                
                <div className="spec-item-desktop">
                  <Icon name="Building" size={18} color="#6b7280" />
                  <div className="spec-content">
                    <div className="spec-label-desktop">Marque/Référence</div>
                    <div className="spec-value-desktop">{produit.brand}</div>
                  </div>
                </div>
                
                <div className="spec-item-desktop">
                  <Icon name="MapPin" size={18} color="#6b7280" />
                  <div className="spec-content">
                    <div className="spec-label-desktop">Localisation</div>
                    <div className="spec-value-desktop">{produit.specifications['Ville'] || produit.specifications['City'] || 'Non spécifié'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <AntiFraudWarning />
        </div>

        {/* Colonne droite - Actions et informations supplémentaires */}
        <div className="right-column">
          <div className="sticky-sidebar">
            <div className="action-card">
              <div className="price-card">
                <div className="price-card-label">Prix</div>
                <div className="price-card-value">{formatPrice(produit.price)}</div>
                {produit.originalPrice && (
                  <div className="price-card-original">
                    Ancien prix: {formatPrice(produit.originalPrice)}
                  </div>
                )}
              </div>

              <div className="action-buttons-desktop">
                <button 
                  className="primary-action-button whatsapp-full"
                  onClick={handleWhatsAppContact}
                >
                  <Icon name="MessageCircle" size={20} color="#fff" />
                  <span>Contacter via WhatsApp</span>
                </button>
                
                <button 
                  className="secondary-action-button"
                  onClick={handlePhoneCall}
                >
                  <Icon name="Phone" size={20} color="#3b82f6" />
                  <span>Appeler le vendeur</span>
                </button>
                
                <button 
                  className="tertiary-action-button"
                  onClick={toggleFavorite}
                >
                  <Icon 
                    name={isFavorite ? "Heart" : "Heart"} 
                    size={20} 
                    color={isFavorite ? "#ef4444" : "#64748b"} 
                    fill={isFavorite ? "#ef4444" : "none"}
                  />
                  <span>{isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}</span>
                </button>
              </div>

              <div className="protection-info">
                <div className="protection-header">
                  <Icon name="ShieldCheck" size={18} color="#10b981" />
                  <span>Achetez en toute sécurité</span>
                </div>
                <ul className="protection-list-desktop">
                  <li>✅ Paiement sécurisé</li>
                  <li>✅ Protection acheteur</li>
                  <li>✅ Support client 7j/7</li>
                </ul>
              </div>
            </div>

      
          </div>
        </div>
      </div>

      {/* Section produits similaires en bas */}
      <div className="related-products-section">
        <div className="section-header">
          <h2 className="section-title-large">Produits similaires</h2>
          <button 
            className="view-all-button"
            onClick={() => navigate('/rechercher', { 
              state: { category: produit.categorieId, categoryName: produit.category }
            })}
          >
            Voir tout
          </button>
        </div>
        <RelatedProducts
          currentProductId={produit.id}
          category={produit.categorieId}
          brand={produit.brand}
        />
      </div>

      {/* Barre d'actions mobile (cachée sur desktop) */}
      <div className="action-bar-mobile">
        <div className="action-buttons">
          <button 
            className="action-button"
            onClick={handleShareProduct}
          >
            <Icon name="Share2" size={22} color="#3b82f6" />
            <div className="action-button-text">
              Partager
            </div>
          </button>
          
          <button 
            className="action-button"
            onClick={() => navigate('/favoris')}
          >
            <Icon name="Heart" size={22} color="#ef4444" />
            <div className="action-button-text">
              Favoris
            </div>
          </button>
          
          <button 
            className="action-button"
            onClick={handlePhoneCall}
          >
            <Icon name="Phone" size={22} color="#3b82f6" />
            <div className="action-button-text">
              Appeler
            </div>
          </button>
        </div>
        
        <button 
          className="contact-button whatsapp"
          onClick={handleWhatsAppContact}
        >
          <div className="contact-button-content">
            <Icon name="MessageCircle" size={22} color="#fff" />
            <div className="contact-button-text">
              WhatsApp
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ProductDetails;